import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { AiOutlineHeart, AiFillHeart, AiOutlineEye, AiOutlineLink, AiOutlineClose } from "react-icons/ai";
import Header from "../components/Header";
import Footer from "../components/Footer";
import Adbanner from "../assets/sidebaradbanner.png";

// Social media share buttons
import {
  FacebookShareButton,
  TwitterShareButton,
  WhatsappShareButton,
  LinkedinShareButton,
  FacebookIcon,
  TwitterIcon,
  WhatsappIcon,
  LinkedinIcon
} from "react-share";
import { FaInstagram, FaFacebook, FaTwitter, FaLinkedin } from "react-icons/fa";

const formatNumber = (num) => {
  if (num >= 1_000_000) return (num / 1_000_000).toFixed(1).replace(/\.0$/, '') + 'M';
  if (num >= 1_000) return (num / 1_000).toFixed(1).replace(/\.0$/, '') + 'K';
  return num.toString();
};

const SocialMediaRow = ({ facebook, twitter, instagram, linkedin }) => {
   const hasSocialMedia = facebook || twitter || instagram || linkedin;
  
  // If no social media data, return null (hide the row)
  if (!hasSocialMedia) return null;
  return (
    <tr className="even:bg-gray-100">
      <td className="px-4 py-2 font-medium text-gray-700 w-1/3">Follow Me</td>
      <td className="px-4 py-2 text-gray-800">
        <div className="flex justify-between items-center">
          
          <div className="flex gap-3">
            {facebook && (
              <a
                href={facebook.startsWith('http') ? facebook : `https://facebook.com/${facebook}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:text-blue-800"
                title="Facebook"
              >
                <FaFacebook size={20} />
              </a>
            )}
            {twitter && (
              <a
                href={twitter.startsWith('http') ? twitter : `https://twitter.com/${twitter}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-400 hover:text-blue-600"
                title="Twitter"
              >
                <FaTwitter size={20} />
              </a>
            )}
            {instagram && (
              <a
                href={instagram.startsWith('http') ? instagram : `https://instagram.com/${instagram}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-pink-600 hover:text-pink-800"
                title="Instagram"
              >
                <FaInstagram size={20} />
              </a>
            )}
            {linkedin && (
              <a
                href={linkedin.startsWith('http') ? linkedin : `https://linkedin.com/in/${linkedin}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-700 hover:text-blue-900"
                title="LinkedIn"
              >
                <FaLinkedin size={20} />
              </a>
            )}
          </div>
        </div>
      </td>
    </tr>
  );
};

function PublicProfile() {
  const { name: slugParam } = useParams();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [liked, setLiked] = useState(false);
  const [likes, setLikes] = useState(0);
  const [copied, setCopied] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentImage, setCurrentImage] = useState("");

  useEffect(() => {
    const profileId = slugParam.split("-").pop();

    // First fetch the profile data
    fetch(`/php-api/get-profile.php?id=${profileId}`)
      .then((res) => res.json())
      .then((data) => {
        if (data.success && Array.isArray(data.data)) {
          const matchedProfile = data.data.find((p) => String(p.id) === profileId);
          if (matchedProfile) {
            setProfile(matchedProfile);
            setLikes(parseInt(matchedProfile.like) || 0);

            // Check if already liked in localStorage
            const likedProfiles = JSON.parse(localStorage.getItem("likedProfiles") || "[]");
            setLiked(likedProfiles.includes(matchedProfile.id));
          }
        }
        setLoading(false);
      })
      .catch(error => {
        console.error("Error fetching profile:", error);
        setLoading(false);
      });

    // Then increment the view count
    fetch(`/php-api/increment-views.php?id=${profileId}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      }
    })
    .catch(error => console.error("Error incrementing view count:", error));

  }, [slugParam]);

  const handleLikeToggle = () => {
    if (!profile) return;
    const profileId = profile.id;
    const likedProfiles = JSON.parse(localStorage.getItem("likedProfiles") || "[]");

    let updatedLikes = likes;
    let updatedLiked = liked;

    if (liked) {
      updatedLikes = Math.max(likes - 1, 0);
      updatedLiked = false;
      localStorage.setItem(
        "likedProfiles",
        JSON.stringify(likedProfiles.filter((id) => id !== profileId))
      );
    } else {
      updatedLikes = likes + 1;
      updatedLiked = true;
      localStorage.setItem(
        "likedProfiles",
        JSON.stringify([...likedProfiles, profileId])
      );
    }

    setLikes(updatedLikes);
    setLiked(updatedLiked);

    // Send like/unlike to backend
    fetch(`/php-api/update-like.php?id=${profileId}&like=${updatedLiked ? 1 : 0}`);
  };

  const copyProfileLink = () => {
    navigator.clipboard.writeText(window.location.href)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      })
      .catch(err => {
        console.error('Failed to copy: ', err);
      });
  };

  const openLightbox = (imageUrl) => {
    setCurrentImage(imageUrl);
    setLightboxOpen(true);
    document.body.style.overflow = 'hidden'; // Prevent scrolling when lightbox is open
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
    document.body.style.overflow = 'auto'; // Re-enable scrolling
  };

  if (loading) return <div className="text-center mt-10">Loading profile...</div>;
  if (!profile) return <div className="text-center mt-10 text-red-500">Profile not found</div>;

  const personalFields = [
    ["Nickname", profile.nickname],
    ["Date of Birth", profile.dob],
    ["Email", profile.email],
    ["Religion", profile.religion],
    ["Qualification", profile.eduqualification],
    ["School", profile.school],
    ["College", profile.college],
    ["Hobbies", profile.hobbies],
    ["Blood Group", profile.bloodgroup],
    ["Hometown", profile.hometown],
    ["Current City", profile.currentcity],
    ["Nationality", profile.nationality],
    ["List of Awards & Honour", profile.awards],
  ];

  const professionalFields = [
    ["Company Name", profile.companyname],
    ["Founder Name", profile.foundername],
    ["Industry", profile.catagory],
    ["Products", profile.products],
    ["Started Year", profile.startedyear],
    ["Board of Members", profile.boardmembers],
    ["Employee", profile.employee],
    ["GST Number", profile.gst],
    ["Head Office", profile.address],
    ["Website", profile.website],
    ["Phone", profile.phone],
    ["Email", profile.email],
    ["List of Awards & Honour", profile.awards],
  ];

  const fieldsToShow = profile.profile === "personal" ? personalFields : professionalFields;

  // Social share variables
  const shareUrl = window.location.href;
  const shareTitle =
    profile.profile === "personal"
      ? profile.name
      : profile.companyname || "Profile";

  return (
    <>
      <Header />
      <div className="page-body-container px-4 py-6 bg-gray-100">
        <div className="max-w-6xl mx-auto mb-4">
          <button onClick={() => navigate("/")}>← Back</button>
        </div>

        <div className="max-w-6xl mx-auto flex flex-col lg:flex-row gap-6">
          <div className="w-full lg:w-4/5 bg-white shadow-md rounded-lg p-6">
            <div className="flex flex-col sm:flex-row items-center gap-6">
              <div 
                className="cursor-pointer transform transition-transform hover:scale-105 w-32 h-32"
                onClick={() => openLightbox(`https://www.giffypedia.com/uploads/${profile.avatar}`)}
              >
                <img
                  src={`https://www.giffypedia.com/uploads/${profile.avatar}`}
                  alt="avatar"
                  className="rounded-full border shadow"
                />
              </div>
              <div className="text-center sm:text-left space-y-1">
                {profile.profile === "personal" ? (
                  <>
                    <h2 className="text-2xl font-semibold text-gray-800 flex items-center gap-2 max-sm:justify-center">{profile.name}  
                      <img
                      src={`https://www.giffypedia.com/uploads/${profile.verified || 'not-verified.jpg'}`}
                      alt={profile.name}
                      className="profile-image"
                    /></h2>
                    <p className="text-gray-600">Profession: {profile.profession || "NA"}</p>
                  </>
                ) : (
                  <>
                    <h2 className="text-2xl font-semibold text-gray-800 flex items-center gap-2 max-sm:justify-center">{profile.companyname || "NA"}
                       <img
                      src={`https://www.giffypedia.com/uploads/${profile.verified || 'not-verified.jpg'}`}
                      alt={profile.name}
                      className="profile-image"
                    />
                    </h2>
                    <p className="text-gray-600">
                      {profile.foundername ? `Founder: ${profile.foundername}` : "Founder: NA"}
                    </p>
                  </>
                )}
                <div className="flex gap-1 items-center mt-2 text-gray-700 max-sm:justify-center">
                  <button onClick={handleLikeToggle} className="text-xl">
                    {liked ? (
                      <AiFillHeart className="text-red-500" />
                    ) : (
                      <AiOutlineHeart className="text-gray-600 hover:text-red-500" />
                    )}
                  </button>
                  <span>{formatNumber(likes)} Likes</span>
                  <span className="flex items-center gap-1 ml-2"><AiOutlineEye /> {formatNumber(profile.view)} Views</span>
                </div>

                {/* Social share buttons */}
                <div className="flex items-center gap-3 sm:mt-3 !mt-5 flex-wrap">
                  <span className="font-medium text-gray-700">Share:</span>
                  <FacebookShareButton url={shareUrl} quote={shareTitle}>
                    <FacebookIcon size={32} round />
                  </FacebookShareButton>
                  <TwitterShareButton url={shareUrl} title={shareTitle}>
                    <TwitterIcon size={32} round />
                  </TwitterShareButton>
                  <WhatsappShareButton url={shareUrl} title={shareTitle} separator=" - ">
                    <WhatsappIcon size={32} round />
                  </WhatsappShareButton>
                  <LinkedinShareButton url={shareUrl}>
                    <LinkedinIcon size={32} round />
                  </LinkedinShareButton>
                  {/* Instagram */}
                  <button
                    onClick={() => window.open(`https://www.instagram.com/?url=${encodeURIComponent(shareUrl)}`, "_blank")}
                    className="bg-pink-500 hover:bg-pink-600 text-white rounded-full p-1"
                    title="Share on Instagram"
                  >
                    <FaInstagram size={25} />
                  </button>
                  
                  {/* Copy Profile Link Button */}
                  <button
                    onClick={copyProfileLink}
                    className={`flex items-center gap-1 px-2 py-1 rounded-md ${copied ? 'bg-green-500 text-white' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'}`}
                    title="Copy profile link"
                  >
                    <AiOutlineLink />
                    <span className="text-sm">{copied ? 'Copied!' : 'Copy Link'}</span>
                  </button>
                </div>
              </div>
            </div>

            {profile.message && (
              <div className="mt-6">
                <h3 className="text-xl font-semibold text-green-700 mb-2">About</h3>
                <p className="text-gray-800 whitespace-pre-line">{profile.message}</p>
              </div>
            )}

            <div className="mt-8">
              <h3 className="text-xl font-semibold text-green-700 mb-3">Details</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full border border-gray-300 rounded text-sm">
                  <tbody>
                    {fieldsToShow
                      .filter(([_, value]) => value && String(value).trim() !== "")
                      .map(([label, value]) => {
                        let displayValue = value;
                        let link = null;

                        const lowerLabel = label.toLowerCase();

                        if (lowerLabel === "email") {
                          link = `mailto:${value}`;
                        } else if (lowerLabel === "website") {
                          link = value.startsWith("http") ? value : `https://${value}`;
                        }

                        return (
                          <tr key={label} className="even:bg-gray-100">
                            <td className="px-4 py-2 font-medium text-gray-700 w-1/3">{label}</td>
                            <td className="px-4 py-2 text-gray-800">
                              {link ? (
                                <a
                                  href={link}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-blue-600 underline break-all"
                                >
                                  {value}
                                </a>
                              ) : (
                                displayValue
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    <SocialMediaRow 
                      facebook={profile.facebook} 
                      twitter={profile.twitter} 
                      instagram={profile.instagram} 
                      linkedin={profile.linked} 
                    />
                  </tbody>
                </table>
              </div>
            </div>

            <div className="mt-8">
              <div className="flex gap-6 justify-center sm:justify-start max-sm:flex-col ">
                {profile.avatar1 && (
                  <div 
                    className="cursor-pointer transform transition-transform hover:scale-105"
                    onClick={() => openLightbox(`https://www.giffypedia.com/uploads/${profile.avatar1}`)}
                  >
                    <img
                      src={`https://www.giffypedia.com/uploads/${profile.avatar1}`}
                      alt="avatar1"
                      className="sm:w-60 w-full rounded-lg border shadow"
                    />
                  </div>
                )}
                {profile.avatar2 && (
                  <div 
                    className="cursor-pointer transform transition-transform hover:scale-105"
                    onClick={() => openLightbox(`https://www.giffypedia.com/uploads/${profile.avatar2}`)}
                  >
                    <img
                      src={`https://www.giffypedia.com/uploads/${profile.avatar2}`}
                      alt="avatar2"
                      className="sm:w-60 w-full rounded-lg border shadow"
                    />
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="w-full lg:w-1/5">
            <a href="https://www.kalamsworldrecords.com" target="_blank" rel="noopener noreferrer">
              <img
                src={Adbanner}
                alt="Ad banner"
                className="w-full rounded-md shadow-md sticky"
              />
            </a>
          </div>
        </div>
      </div>
      <Footer />

      {/* Lightbox Modal */}
      {lightboxOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center p-4"
          onClick={closeLightbox}
        >
          <div className="relative max-w-4xl max-h-full">
            <button
              onClick={closeLightbox}
              className="absolute -right-11 text-white text-3xl hover:text-gray-300 transition-colors z-10"
            >
              <AiOutlineClose className="text-3xl" />
            </button>
            <div className="max-w-full max-h-screen flex items-center justify-center">
              <img
                src={currentImage}
                alt="Enlarged view"
                className="max-w-full max-h-screen object-contain rounded"
                onClick={(e) => e.stopPropagation()}
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default PublicProfile;